import React, { useRef, useEffect, useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';

import MouseIcon from '@mui/icons-material/Mouse';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CodeIcon from '@mui/icons-material/Code';
import HistoryIcon from '@mui/icons-material/History';
import { SiGit } from 'react-icons/si';

interface ToolbarErdProps {
  tool: string;
  setTool: (tool: string) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  fitToBoard: () => void;
  centerToSelection: () => void;
  onSaveDiagram?: () => void;
  onPublishDiagram?: () => void;
  isPublishing?: boolean;
  onShowHistory?: () => void;
  diagramGitlabPath?: string | null;
  elements?: unknown[];
  topOffset?: number;
  onExportImage?: (fmt: string) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  showCodePanel?: boolean;
  setShowCodePanel?: (v: boolean) => void;
  onSyncToCode?: () => void;
  onBeforeNavigate?: () => boolean;
}

const iconStyle = { width: 18, height: 18 };
const EMPTY_ELEMENTS: unknown[] = [];
const EMPTY_STYLE: React.CSSProperties = {};

const ToolbarButton: React.FC<{
  theme: any;
  title: string;
  onClick: () => void;
  active?: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ theme, title, onClick, active = false, style = EMPTY_STYLE, children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    style={{
      backgroundColor: active ? theme.toolbar.buttonBackgroundActive : theme.toolbar.buttonBackground,
      color: theme.toolbar.buttonText,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: theme.toolbar.buttonBorder,
      borderRadius: 6,
      padding: '6px 8px',
      cursor: 'pointer',
      fontSize: 14,
      minWidth: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      ...style,
    }}
    onMouseEnter={(e) => {
      if (!active) (e.currentTarget as HTMLButtonElement).style.backgroundColor =
        theme.name === 'light' ? '#f1f5f9' : '#374151';
    }}
    onMouseLeave={(e) => {
      if (!active) (e.currentTarget as HTMLButtonElement).style.backgroundColor =
        active ? theme.toolbar.buttonBackgroundActive : theme.toolbar.buttonBackground;
    }}
  >
    {children}
  </button>
);

const ToolbarDivider: React.FC<{ border: string }> = ({ border }) => (
  <div style={{ width: 1, height: 24, background: border, margin: '0 4px' }} />
);

const ToolbarErd: React.FC<ToolbarErdProps> = ({
  tool,
  setTool,
  zoomIn,
  zoomOut,
  fitToBoard,
  centerToSelection,
  onSaveDiagram = () => {},
  onPublishDiagram,
  isPublishing = false,
  onShowHistory,
  diagramGitlabPath,
  elements = EMPTY_ELEMENTS,
  topOffset = 0,
  onExportImage,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  showCodePanel,
  setShowCodePanel,
  onSyncToCode,
  onBeforeNavigate,
}) => {
  const { theme, toggleTheme, currentTheme } = useTheme();
  const navigate = useNavigate();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const guardedNavigate = (target: number | string) => {
    if (typeof onBeforeNavigate === 'function' && !onBeforeNavigate()) return;
    navigate(target as any);
  };

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  return (
    <div style={{
      position: 'absolute',
      top: topOffset,
      left: 0,
      right: 0,
      height: 56,
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.toolbar.background,
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
      borderBottomColor: theme.toolbar.border,
      padding: '0 16px',
      boxShadow: theme.toolbar.shadow,
    }}>

      {/* Left section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <ToolbarButton theme={theme} title="Назад" onClick={() => guardedNavigate(-1)}>
          <ArrowBackIcon style={iconStyle} />
        </ToolbarButton>

        <div style={{ fontSize: 17, fontWeight: 'bold', color: theme.toolbar.text, marginRight: 8 }}>
          ERD
        </div>

        <ToolbarButton theme={theme} title="Сохранить (Ctrl+S)" onClick={onSaveDiagram}>
          <SaveIcon style={iconStyle} />
        </ToolbarButton>

        {onPublishDiagram && (
          <ToolbarButton
            theme={theme}
            title={isPublishing ? 'Публикуется...' : 'Опубликовать в GitLab'}
            onClick={onPublishDiagram}
            style={{ color: isPublishing ? undefined : '#f05032', borderColor: isPublishing ? undefined : '#f05032', opacity: isPublishing ? 0.6 : 1 }}
          >
            <SiGit style={iconStyle} />
          </ToolbarButton>
        )}

        {onShowHistory && diagramGitlabPath && (
          <ToolbarButton
            theme={theme}
            title="История версий"
            onClick={onShowHistory}
            style={{ color: '#a6e3a1', borderColor: '#40a02b' }}
          >
            <HistoryIcon style={iconStyle} />
          </ToolbarButton>
        )}

        {onExportImage && (
          <div style={{ position: 'relative' }} ref={exportMenuRef}>
            <ToolbarButton theme={theme} title="Экспорт" onClick={() => setShowExportMenu(!showExportMenu)} active={showExportMenu}>
              <ImageIcon style={iconStyle} />
            </ToolbarButton>
            {showExportMenu && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4,
                background: theme.toolbar.background, border: `1px solid ${theme.toolbar.border}`,
                borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 3000, minWidth: 100,
              }}>
                {(['png', 'jpg', 'svg'] as const).map((fmt) => (
                  <button
                    type="button"
                    key={fmt}
                    onClick={() => { onExportImage(fmt); setShowExportMenu(false); }}
                    style={{
                      display: 'block', width: '100%', padding: '8px 16px',
                      backgroundColor: 'transparent', border: 'none',
                      color: theme.toolbar.buttonText, textAlign: 'left',
                      cursor: 'pointer', textTransform: 'uppercase', fontSize: 12,
                    }}
                    onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = theme.name === 'light' ? '#f1f5f9' : '#374151'}
                    onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                  >{fmt}</button>
                ))}
              </div>
            )}
          </div>
        )}

        <ToolbarDivider border={theme.toolbar.border} />

        <ToolbarButton theme={theme} title="Отменить (Ctrl+Z)" onClick={() => canUndo && onUndo?.()}
          style={{ opacity: canUndo ? 1 : 0.4, cursor: canUndo ? 'pointer' : 'not-allowed' }}>
          <UndoIcon style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton theme={theme} title="Повторить (Ctrl+Y)" onClick={() => canRedo && onRedo?.()}
          style={{ opacity: canRedo ? 1 : 0.4, cursor: canRedo ? 'pointer' : 'not-allowed' }}>
          <RedoIcon style={iconStyle} />
        </ToolbarButton>

        <ToolbarDivider border={theme.toolbar.border} />

        <ToolbarButton theme={theme} title="Выбор (S)" onClick={() => setTool('select')} active={tool === 'select'}>
          <MouseIcon style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton theme={theme} title="Связь (A)" onClick={() => setTool('arrow')} active={tool === 'arrow'}>
          <ArrowForwardIcon style={iconStyle} />
        </ToolbarButton>
      </div>

      {/* Center section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <ToolbarButton theme={theme} title="Увеличить (+)" onClick={zoomIn}><ZoomInIcon style={iconStyle} /></ToolbarButton>
        <ToolbarButton theme={theme} title="Уменьшить (-)" onClick={zoomOut}><ZoomOutIcon style={iconStyle} /></ToolbarButton>
        <ToolbarButton theme={theme} title="Вместить всё (0)" onClick={fitToBoard}><CenterFocusStrongIcon style={iconStyle} /></ToolbarButton>
        <ToolbarButton theme={theme} title="К выделенному" onClick={centerToSelection}><GpsFixedIcon style={iconStyle} /></ToolbarButton>
      </div>

      {/* Right section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {onSyncToCode && (
          <ToolbarButton theme={theme} title="Синхронизировать холст → DSL код" onClick={onSyncToCode}
            style={{ fontSize: 12, padding: '6px 10px', gap: 4 }}>
            ← Sync to Code
          </ToolbarButton>
        )}
        {setShowCodePanel && (
          <ToolbarButton
            theme={theme}
            title={showCodePanel ? 'Скрыть код' : 'Показать код'}
            onClick={() => setShowCodePanel(!showCodePanel)}
            active={showCodePanel}
          >
            <CodeIcon style={iconStyle} />
          </ToolbarButton>
        )}
        <ToolbarButton theme={theme} title={`Тема: ${currentTheme}`} onClick={toggleTheme}>
          {currentTheme === 'dark' ? <LightModeIcon style={iconStyle} /> : <DarkModeIcon style={iconStyle} />}
        </ToolbarButton>
        {(elements as unknown[]).length > 0 && (
          <div style={{ color: theme.toolbar.text, fontSize: 12, opacity: 0.7, marginLeft: 4 }}>
            {(elements as unknown[]).length} эл. • ERD
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolbarErd;
